import React from "react";
import useStyles from "./TextBoxControl.styles";
import { DispatchProp } from "react-redux";
import { includes, trim } from "../../utils/lodash";
import { ChoiceListControl } from "./ChoiceListControl";
import { ControlContainer } from "../../containers/controls/ControlContainer";
import { BaseControl, ControlProps } from "./BaseControl";
import { QuestionModel } from "../../models/QuestionModel";
import { QuestionType } from "../../models/enums/QuestionType";
import { ChoiceModel } from "../../models/ChoiceModel";
import { PhoneRegularExpression, EmailRegularExpression } from "../../consts";
import { CaseMode } from "../../models/enums/CaseMode";
import {
    getSettingNumber,
    getSettingBoolean,
    getSettingString,
    hasSetting,
    getSettingEnum,
} from "../../models/utilities/settingUtilities";
import { isNullOrEmpty, isNullOrNaN, removeHtmlTags } from "../../utils";
import { ValidationResultModel } from "../../models/ValidationResultModel";
import { ValidationBuilder } from "../../models/builders/ValidationBuilder";
import { formatNumeric, getNumeral, decimalSeparator } from "../../utils/Number";
import { ValidationOptionsModel } from "../../models/ValidationOptionsModel";
import { translate } from "../../models/utilities/translationUtilities";
import { getRendererProps } from "../renderers/BaseRenderer";
import {
    shouldAutoFormat,
    getSoftPromptCode,
    defaultSize,
    isPastedTextDetectionEnabled,
} from "../../models/utilities/questionUtilities";
import TextAnswerRenderer from "../renderers/v2/TextAnswerRenderer";
import { respondentSetPhone, respondentSetEmail, clearProbedQuestions } from "../../redux/actions";
import { getValidationResults } from "../../redux/thunks/validation";
import { VariableType } from "../../models/enums/VariableType";
import { VariableModel } from "../../models/VariableModel";
import { isQuestionValidatedServerSide } from "../../models/utilities/validationUtilities";
import ProbedQuestionsContainer from "../shared/ProbedQuestionsContainer";
import { __getStore__ } from "../../redux/store";
import { useTheme } from "react-jss";

interface GuidedMessageProps {
    message: string;
    enabled: boolean;
    question: QuestionModel;
    questionType: QuestionType;
}

const GuidedMessage: React.FC<GuidedMessageProps> = ({
    message,
    enabled,
    questionType,
    question,
}) => {
    const theme = useTheme();
    const size = getSettingString(question, "Size", defaultSize(question));
    const classes = useStyles({ theme, size });
    if (
        !enabled ||
        (questionType !== QuestionType.TextAnswer && questionType !== QuestionType.OpenEndAnswer)
    ) {
        return null;
    }

    return <div className={classes.guidedMessage}>{message}</div>;
};

interface Props extends ControlProps, DispatchProp {}

interface State {
    value: string;
    focus: boolean;
    answers: ChoiceModel[];
    input?: HTMLElement;
    probingClearedOnBackspace?: boolean;
    /** Pasted text detection: accumulated pasted content (sent with mention on blur). */
    pastedContent?: string[];
}

export class TextBoxControl extends BaseControl<Props, State> {
    input?: HTMLElement;

    maxResults = (props: Props) => getSettingNumber(props.question, "MaxResults");

    constructor(props: Props) {
        super(props);
        this.state = {
            value: "",
            focus: false,
            answers: undefined,
        };
    }

    getFilteredAnswers(state: State, props: Props, showAllAnswers?: boolean) {
        if (!showAllAnswers) {
            const minSearchLength = getSettingNumber(props.question, "MinSearchLength", 0);
            if (isNullOrEmpty(state.value) || state.value.length < minSearchLength) {
                return [];
            }
        }
        const { question } = props;
        const searchValue = state.value.trim().toLowerCase();
        let choiceList = props.sequencedVariables[0].choiceList.filter(
            (choice) => !isNullOrEmpty(choice.value),
        );
        if (!showAllAnswers) {
            switch (getSettingString(question, "SearchOption")) {
                case "Exact":
                    choiceList = choiceList.filter(
                        (choice) =>
                            this.getChoiceText(choice, props.language).trim().toLowerCase() ===
                            searchValue,
                    );
                    break;
                case "StartsWith":
                    choiceList = choiceList.filter((choice) =>
                        this.getChoiceText(choice, props.language)
                            .trim()
                            .toLowerCase()
                            .startsWith(searchValue),
                    );
                    break;
                default:
                    choiceList = choiceList.filter((choice) =>
                        this.getChoiceText(choice, props.language)
                            .trim()
                            .toLowerCase()
                            .includes(searchValue),
                    );
            }
        }
        choiceList = choiceList.sort((a, b) => {
            if (a === b) {
                return 0;
            }
            return a < b ? -1 : 1;
        });
        if (this.maxResults(props) != null) {
            return choiceList.slice(0, this.maxResults(props));
        }
        return choiceList;
    }

    initializeQuestion(question: QuestionModel, props: Props) {
        super.initializeQuestion(question, props);
        const variable = this.getQuestionVariable(question.variables);
        if (!this.hasMentions(props)) {
            switch (question.type) {
                case QuestionType.RespondentPhone:
                    this.setMentions(this.props, variable.name, (mentions) =>
                        mentions.set(props.phone),
                    );
                    break;
                case QuestionType.RespondentEmail:
                    this.setMentions(this.props, variable.name, (mentions) =>
                        mentions.set(props.email),
                    );
                    break;
                default:
                    break;
            }
        }
        this.updateState(props, false);
    }

    willUpdateMentions(question: QuestionModel, nextProps: Props) {
        super.willUpdateMentions(question, nextProps);
        this.updateState(nextProps, false);
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        super.componentDidUpdate(prevProps, prevState);
        if (prevProps.language !== this.props.language) {
            this.updateState(this.props);
        }
    }

    componentDidMount() {
        this.setState((prevState) => ({ ...prevState, input: this.input }));
    }

    private onAutoFill = (autofilledValue: string) => {
        if (autofilledValue === this.state.value) return;
        this.setState({ value: autofilledValue }, () => this.updateAnswers());
    };

    hasAnswerWithValue(props: Props, value: string) {
        if (isNullOrEmpty(value)) {
            return false;
        }
        const { variables } = props.question;
        const [firstVariable] = variables;
        const { choiceList } = firstVariable;
        return choiceList.some((choice) => choice.value === value);
    }

    hasMentionsInChoices = (props: Props) => {
        const { question } = props;

        const [firstVariable] = question.variables;
        const { choiceList } = firstVariable;

        const hasMentions = this.hasMentions(props);
        const mentions = this.getMentions(props);
        const [firstMention] = mentions;

        const hasMentionInChoices =
            hasMentions &&
            choiceList
                .filter((choice) => !isNullOrEmpty(choice.value))
                .some((choice) => choice.value === firstMention.value);

        return hasMentionInChoices;
    };

    updateState(props: Props, shouldUpdateRespondentFields = true) {
        this.setState((prevState) => {
            const newState = { ...prevState };
            const mention = this.getMention(props);
            const softPromptCode = getSoftPromptCode(this.props.question);
            newState.value = "";
            switch (props.question.type) {
                case QuestionType.RespondentEmail:
                    newState.value = shouldUpdateRespondentFields
                        ? props.email
                        : mention?.value || prevState.value;
                    break;
                case QuestionType.RespondentPhone:
                    newState.value = shouldUpdateRespondentFields
                        ? props.phone
                        : mention?.value || prevState.value;
                    break;
                case QuestionType.OpenEndAnswer:
                    if (mention) {
                        newState.value = mention.openEnd;
                    } else {
                        newState.value = prevState.value || "";
                    }
                    break;
                case QuestionType.NumericAnswer: {
                    const hasAnswerWithValue =
                        mention && this.hasAnswerWithValue(props, mention.value);
                    const hasMentionsInChoices = this.hasMentionsInChoices(props);
                    const isChoiceVisible = mention && this.isChoiceVisible(mention.value, props);
                    if (
                        mention &&
                        !hasAnswerWithValue &&
                        (!hasMentionsInChoices || !isChoiceVisible)
                    ) {
                        const numeral = getNumeral(mention.value).value();
                        if (!isNullOrNaN(numeral)) {
                            newState.value = this.formatNumeric(numeral, props);
                        }
                    }
                    break;
                }
                default:
                    if (mention && mention.value !== softPromptCode) {
                        newState.value = mention.value;
                    } else {
                        newState.value = prevState.value || "";
                    }
                    break;
            }
            return newState;
        });
    }

    onKeyPress = (event: React.KeyboardEvent) => {
        if (this.isKeyPrintable(event.key) && this.hasMentionsInChoices(this.props)) {
            const { question } = this.props;
            const variable = this.getQuestionVariable(question.variables);
            this.setMentions(this.props, variable.name, (mention) => mention.clear());

            // Clear component state to maintain consistency with cleared mention
            this.setState((prevState) => ({
                ...prevState,
                value: "",
            }));
        }
    };

    onKeyDown = (event: React.KeyboardEvent) => {
        const { question } = this.props;
        const variable = this.getQuestionVariable(question.variables);

        // Handle backspace for AI probing questions
        if (
            event.key === "Backspace" &&
            getSettingBoolean(question, "EnableAiProbeContainer") &&
            (question.type === QuestionType.TextAnswer ||
                question.type === QuestionType.OpenEndAnswer) &&
            variable
        ) {
            // Check if there are probed questions to clear
            const store = __getStore__();
            if (store) {
                const state = store.getState();
                const probedQuestions =
                    state.interview?.variables?.probedQuestions?.[variable.elementId];

                // If there are probed questions, clear them and let backspace proceed normally
                if (probedQuestions && probedQuestions.length > 0) {
                    // Clear probed questions AND associated AI_PROBING mention variables
                    this.props.dispatch(clearProbedQuestions(variable.elementId));

                    // Clear all AI_PROBING mention variables
                    this.setMentions(this.props, `${variable.name}_AI_PROBING`, (mentions) => mentions.clear());
                    this.setMentions(this.props, `${variable.name}_AI_PROBING_QUESTIONS`, (mentions) => mentions.clear());
                    this.setMentions(this.props, `${variable.name}_AI_PROBING_ANSWERS`, (mentions) => mentions.clear());
                    this.setMentions(this.props, `${variable.name}_AI_PROBING_QUESTIONS_1`, (mentions) => mentions.clear());
                    this.setMentions(this.props, `${variable.name}_AI_PROBING_ANSWERS_1`, (mentions) => mentions.clear());

                    // Set flag to prevent duplicate clearing in onTextChanged
                    this.setState((prevState) => ({
                        ...prevState,
                        probingClearedOnBackspace: true,
                    }));
                }
            }
        }
    };

    isKeyPrintable = (key: string) => key.length === 1;

    onTextChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        let { value } = event.target;
        const { maxLength } = event.target;
        const { question, language } = this.props;
        const caseMode = getSettingEnum(question, CaseMode, "CaseMode");
        // @ts-ignore - Variable used in other methods but not here
        const variable = this.getQuestionVariable(question.variables);

        const numericQuestionTypes = [QuestionType.NumericAnswer, QuestionType.RunningTotal];
        const isNumeric = numericQuestionTypes.includes(question.type);

        if (isNumeric) {
            const { NumberOfDecimals: nbDecimalDefine } = question.settings;
            const delimiter = decimalSeparator(language);

            if (includes(value, ".") && delimiter === ",") {
                value = value.replace(".", ",");
            }

            const parts = value.split(delimiter, 2);
            const [wholeNumber, decimalNumber] = parts;

            if (nbDecimalDefine === undefined || nbDecimalDefine === 0) {
                if (includes(value, delimiter)) {
                    return;
                }
                value = wholeNumber;
            } else if (includes(value, delimiter) && decimalNumber) {
                if (decimalNumber.length > nbDecimalDefine) {
                parts[1] = decimalNumber.substring(0, nbDecimalDefine);
                }
                value = parts.join(delimiter);
            }

            let invalidNumber;
            if (question.parent && question.parent.type === QuestionType.RunningTotal) {
                invalidNumber = value.match(/[^\d. ,-]|[.,]{2,}/) || includes(value, "-");
            } else {
                invalidNumber =
                    value.match(/[^\d. ,-]|[.,]{2,}/) ||
                    Array.from(value).find((char, index) => index > 0 && char === "-");
            }
            if (invalidNumber) {
                return;
            }
        }

        if (caseMode === CaseMode.AllCaps) {
            value = value.toUpperCase();
        } else if (caseMode === CaseMode.FirstLetterCaps) {
            value = value
                .split(" ")
                .map((v) => v.charAt(0).toUpperCase() + v.slice(1))
                .join(" ");
        }

        if (maxLength > -1 && value.length > maxLength) {
            value = value.substring(0, maxLength);
        }

        // For questions with AI probing enabled, when the answer changes
        if (
            getSettingBoolean(question, "EnableAiProbeContainer") &&
            (question.type === QuestionType.TextAnswer ||
                question.type === QuestionType.OpenEndAnswer) &&
            variable
        ) {
            // If value has changed from previous state, clear follow-up probed questions
            if (this.state.value !== value) {
                // Get state to find any probed questions
                const store = __getStore__();
                if (store) {
                    // Clear probed questions AND associated AI_PROBING mention variables
                    this.props.dispatch(clearProbedQuestions(variable.elementId));

                    // Also clear the AI_PROBING mention variables that were created during probing
                    // These leftover mentions can interfere with validation after probing
                    const legacyProbeVariableName = `${variable.name}_AI_PROBING`;
                    const probeQuestionsVariableName = `${variable.name}_AI_PROBING_QUESTIONS`;
                    const probeAnswersVariableName = `${variable.name}_AI_PROBING_ANSWERS`;

                    this.setMentions(this.props, legacyProbeVariableName, (mentions) =>
                        mentions.clear(),
                    );
                    this.setMentions(this.props, probeQuestionsVariableName, (mentions) =>
                        mentions.clear(),
                    );
                    this.setMentions(this.props, probeAnswersVariableName, (mentions) =>
                        mentions.clear(),
                    );
                }
            }
        }

        this.setState((prevState) => {
            const newState = { ...prevState };
            newState.value = value;
            newState.probingClearedOnBackspace = false; // Reset the flag
            return newState;
        });
    };

    formatNumeric(value: string | number, props: Props, cultureInvariant?: boolean) {
        const { question } = props;
        return formatNumeric(
            value,
            getSettingNumber(question, "NumberOfDecimals"),
            props.language,
            cultureInvariant,
            this.shouldFormat(question),
        );
    }

    shouldFormat(question: QuestionModel) {
        return this.isNumericAnswer(question) && shouldAutoFormat(question);
    }

    isNumericAnswer(question: QuestionModel) {
        return question.type === QuestionType.NumericAnswer;
    }

    updateAnswers() {
        const { question, language } = this.props;
        const variable = this.getQuestionVariable(question.variables);
        let { value } = this.state;

        const hasMentionInChoices = this.hasMentionsInChoices(this.props);
        if (isNullOrEmpty(value) && hasMentionInChoices) {
            return;
        }

        if (this.isNumericAnswer(question)) {
            const numeral = getNumeral(value, language).value();
            if (!isNullOrNaN(numeral)) {
                value = this.formatNumeric(numeral, this.props, true);
            }
        }

        if (question.type === QuestionType.OpenEndAnswer) {
            value = trim(value || "");
            const answerCode = getSettingString(question, "AnswerCode", "0");
            const newPastedContent =
                isPastedTextDetectionEnabled(question, this.props.questionnaire) &&
                this.state.pastedContent?.length
                    ? this.state.pastedContent
                    : undefined;
            if (isNullOrEmpty(value)) {
                this.setState({ pastedContent: [] });
            }
            this.updateMention(value, variable, answerCode, newPastedContent);
            if (newPastedContent?.length && !isNullOrEmpty(value)) {
                this.setState({ pastedContent: [] });
            }
            return;
        }

        if (question.type === QuestionType.TextAnswer) {
            const newPastedContent =
                isPastedTextDetectionEnabled(question, this.props.questionnaire) &&
                this.state.pastedContent?.length
                    ? this.state.pastedContent
                    : undefined;
            if (isNullOrEmpty(value)) {
                this.setState({ pastedContent: [] });
            }
            this.updateMention(value, variable, undefined, newPastedContent);
            if (newPastedContent?.length && !isNullOrEmpty(value)) {
                this.setState({ pastedContent: [] });
            }
            return;
        }

        this.updateMention(value, variable);
    }

    updateMention = (
        value: string,
        variable: VariableModel,
        code?: string,
        newPastedContent?: string[],
    ) => {
        const mention = this.getMentions(this.props, variable.name)?.find((m) =>
            this.mentionIsValid(m, this.props.question),
        );

        // Merge new pasted content with existing so we don't overwrite previous pastes in the request
        const existingPastedContent = mention?.pastedContent ?? [];
        const mergedPastedContent =
            newPastedContent !== undefined
                ? [...existingPastedContent, ...newPastedContent]
                : existingPastedContent.length > 0
                  ? existingPastedContent
                  : undefined;

        // Only persist a mention when there is a real answer (non-empty value/openEnd).
        // Do not send a mention with only pastedContent — it breaks backend validation (answers: [] when no answer).
        if (isNullOrEmpty(value)) {
            if (mention) {
                this.setMentions(this.props, variable.name, (mentions) => mentions.clear());
            }
            return;
        }
        if (mention?.value !== value || mergedPastedContent !== undefined) {
            this.setMentions(this.props, variable.name, (mentions) => {
                if (code !== undefined) {
                    return mentions.set(code, value, mergedPastedContent);
                }
                return mergedPastedContent?.length
                    ? mentions.set(value, undefined, mergedPastedContent)
                    : mentions.set(value);
            });
        }
    };

    onTextBlur = () => {
        this.updateAnswers();
    };

    placeholder() {
        return "";
    }

    inputtype(question: QuestionModel) {
        switch (question.type) {
            case QuestionType.RespondentEmail:
            case QuestionType.EmailAddressAnswer:
                return "email";
            case QuestionType.RespondentPhone:
                return "tel";
            default:
                return "text";
        }
    }

    maxLength = (question: QuestionModel) => getSettingNumber(question, "MaxAnswerLength");

    minValue() {
        const min = super.minValue();
        return min != null ? min : Number.MIN_SAFE_INTEGER;
    }

    maxValue() {
        const max = super.maxValue();
        return max != null ? max : Number.MAX_SAFE_INTEGER;
    }

    displayValue = () => {
        const mentionValue = this.getMentionValue(this.props);

        const isValueMentioned =
            this.state.value === mentionValue && this.hasAnswerWithValue(this.props, mentionValue);

        if (this.state.value === undefined || isValueMentioned) {
            return "";
        }
        return this.state.value;
    };

    textAlign = (question: QuestionModel) => {
        if (question.type === QuestionType.NumericAnswer) {
            return "right";
        }
        return undefined;
    };

    renderQuestion(question: QuestionModel) {
        const multiline =
            getSettingBoolean(question, "MultiLine") ||
            question.type === QuestionType.OpenEndAnswer;

        const lines = getSettingNumber(question, "Lines");
        const enabledAIProbing = getSettingBoolean(question, "EnableAiProbeContainer");
        const enabledChoiceListContainer =
            !hasSetting(question, "EnableChoiceListContainer") ||
            getSettingBoolean(question, "EnableChoiceListContainer");
        const guidedMessage = translate(question, "GuidanceMessage", this.props.language);
        const variable = this.getQuestionVariable(question.variables);
        const pasteDetectionEnabled =
            (question.type === QuestionType.OpenEndAnswer ||
                question.type === QuestionType.TextAnswer) &&
            isPastedTextDetectionEnabled(question, this.props.questionnaire);

        return (
            <>
                <TextAnswerRenderer
                    {...getRendererProps(this.props)}
                    multiline={multiline}
                    inputType={this.inputtype(question)}
                    inputMode={question.type === QuestionType.NumericAnswer ? "decimal" : undefined}
                    placeholder={this.placeholder()}
                    inputRef={(r) => {
                        this.input = r as HTMLInputElement;
                    }}
                    min={this.minValue()}
                    max={this.maxValue()}
                    maxLength={this.maxLength(question)}
                    onBlur={this.onTextBlur}
                    onChange={this.onTextChanged}
                    onKeyDown={this.onKeyDown}
                    onKeyPress={this.onKeyPress}
                    onAutoFill={this.onAutoFill}
                    onPaste={
                        pasteDetectionEnabled
                            ? (pastedText) => {
                                  if (pastedText) {
                                      this.setState((prev) => ({
                                          pastedContent: [
                                              ...(prev.pastedContent || []),
                                              pastedText,
                                          ],
                                      }));
                                  }
                              }
                            : undefined
                    }
                    value={this.displayValue()}
                    disabled={this.props.isDisabled}
                    lines={lines}
                />

                <GuidedMessage
                    message={guidedMessage}
                    enabled={enabledAIProbing}
                    questionType={question.type}
                    question={question}
                />

                {/* The container for probed questions */}
                {enabledAIProbing &&
                    (question.type === QuestionType.TextAnswer ||
                        question.type === QuestionType.OpenEndAnswer) && (
                        <ProbedQuestionsContainer
                            mainVariableId={variable.elementId}
                            rendererProps={getRendererProps(this.props)}
                            multiline={multiline}
                            maxLength={this.maxLength(question)}
                            lines={lines}
                            disabled={this.props.isDisabled}
                            settings={question.settings}
                        />
                    )}

                {enabledChoiceListContainer && (
                    <ControlContainer
                        controlType={ChoiceListControl}
                        {...getRendererProps(this.props)}
                    />
                )}
            </>
        );
    }

    regexForValidationMask(mask: string) {
        let regex = "";
        const replacements = {
            A: "[A-Z]",
            Z: "[A-Z0-9]",
            9: "[0-9]",
            "*": ".",
        };
        Array.from(mask).forEach((c) => {
            regex += replacements[c] || `\\${c}`;
        });
        return `^${regex}$`;
    }

    validateFormat(question: QuestionModel, value: string) {
        const mask = getSettingString(question, "ValidationMask");
        if (isNullOrEmpty(mask)) {
            return true;
        }
        return new RegExp(this.regexForValidationMask(mask), "i").test(value);
    }

    shouldEnforceValidation(question: QuestionModel) {
        return (
            !hasSetting(question, "EnforceValidation") ||
            getSettingBoolean(question, "EnforceValidation")
        );
    }

    isAnswerComplete() {
        // Check if we have a valid text input value
        const hasTextValue = !isNullOrEmpty(trim(this.state.value));

        // If we have text input, the answer is complete regardless of mention state
        if (hasTextValue) {
            return true;
        }

        // If no text input, check for valid choice mentions
        if (this.hasMentions(this.props)) {
            const [mention] = this.getMentions(this.props);
            const variable = this.getQuestionVariable(this.props.sequencedVariables);
            const { choiceList = [] } = variable;
            if (choiceList.some((choice) => choice.value === mention.value)) {
                return true;
            }
        }
        return false;
    }

    validate(options?: ValidationOptionsModel): ValidationResultModel {
        this.updateAnswers();
        const { question, useClientValidation, dispatch } = this.props;
        const { value } = this.state;

        const validation = new ValidationBuilder();
        if (isQuestionValidatedServerSide(question.type, useClientValidation)) {
            if (question.type === QuestionType.RespondentPhone) {
                dispatch(respondentSetPhone(value));
            } else if (question.type === QuestionType.RespondentEmail) {
                dispatch(respondentSetEmail(value));
            }
            return validation.build();
        }

        const variable = question.variables.find((v) => v.type !== VariableType.Computed);
        const hasSelectedAnswer = this.hasAnswerWithValue(this.props, value);
        let result = super.validate(options);

        if (result.isValid) {
            if (!isNullOrEmpty(value) && !hasSelectedAnswer) {
                switch (question.type) {
                    case QuestionType.OpenEndAnswer:
                        result = this.validateOpenEnd(value, question);
                        if (!result.isValid) {
                            return result;
                        }
                        break;
                    case QuestionType.RespondentPhone:
                        if (
                            this.shouldEnforceValidation(question) &&
                            !PhoneRegularExpression.test(value)
                        ) {
                            return validation.fail(variable.name, "InvalidAnswer").build();
                        }
                        this.props.dispatch(respondentSetPhone(value));
                        break;
                    case QuestionType.RespondentEmail:
                        if (
                            this.shouldEnforceValidation(question) &&
                            !EmailRegularExpression.test(value)
                        ) {
                            return validation.fail(variable.name, "InvalidEmailAddress").build();
                        }
                        this.props.dispatch(respondentSetEmail(value));
                        break;
                    case QuestionType.EmailAddressAnswer:
                        if (
                            this.shouldEnforceValidation(question) &&
                            !EmailRegularExpression.test(value)
                        ) {
                            return validation.fail(variable.name, "InvalidEmailAddress").build();
                        }
                        break;
                    case QuestionType.TextAnswer:
                        if (!this.validateFormat(question, value)) {
                            return validation.fail(variable.name, "InvalidTextFormat").build();
                        }
                        break;
                    case QuestionType.NumericAnswer:
                        return getValidationResults([question]);
                    default:
                        break;
                }
            }
        }

        return result;
    }
}
